import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MarketplaceSEO } from '../components/SEO';
import { 
  Search, Filter, Grid3X3, List, ChevronDown, X, Heart, 
  GitCompare, Globe, Link as LinkIcon, AlertCircle, ArrowRight,
  ArrowUpDown, TrendingUp, Zap, Check, SlidersHorizontal,
  Newspaper, Share2, Printer, Package, Users
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service Type Tabs Configuration
const SERVICE_TABS = [
  { id: 'all', label: 'All Services', icon: Globe },
  { id: 'digital_pr', label: 'Digital PR', icon: Newspaper },
  { id: 'social_media', label: 'Social Media', icon: Share2 },
  // { id: 'print_media', label: 'Print Media', icon: Printer },  // TEMPORARILY HIDDEN - Re-enable when Print Media is ready
  { id: 'bundles', label: 'Bundles', icon: Package },
];

// Skeleton Loader Component
const PublicationCardSkeleton = () => (
  <div className="border border-[#e5e5e5] p-6 bg-white animate-pulse">
    <div className="h-16 mb-4 flex items-center">
      <div className="h-12 w-32 bg-gray-200 rounded"></div>
    </div>
    <div className="flex gap-2 mb-4">
      <div className="h-6 w-20 bg-gray-200 rounded"></div>
      <div className="h-6 w-16 bg-gray-200 rounded"></div>
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-4 w-full bg-gray-200 rounded"></div>
      <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
    </div>
    <div className="flex gap-2 mb-4">
      <div className="h-6 w-16 bg-gray-200 rounded"></div>
      <div className="h-6 w-20 bg-gray-200 rounded"></div>
    </div>
    <div className="space-y-2 mb-6">
      <div className="flex justify-between">
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
        <div className="h-5 w-24 bg-gray-200 rounded"></div>
      </div>
    </div>
    <div className="h-12 w-full bg-gray-200 rounded"></div>
  </div>
);

// List View Skeleton
const PublicationListSkeleton = () => (
  <div className="border border-[#e5e5e5] p-4 bg-white animate-pulse flex items-center gap-6">
    <div className="h-12 w-32 bg-gray-200 rounded flex-shrink-0"></div>
    <div className="flex-1 space-y-2">
      <div className="h-5 w-48 bg-gray-200 rounded"></div>
      <div className="h-4 w-full bg-gray-200 rounded"></div>
    </div>
    <div className="flex gap-2">
      <div className="h-6 w-16 bg-gray-200 rounded"></div>
      <div className="h-6 w-16 bg-gray-200 rounded"></div>
    </div>
    <div className="text-right">
      <div className="h-5 w-24 bg-gray-200 rounded"></div>
    </div>
    <div className="h-10 w-32 bg-gray-200 rounded"></div>
  </div>
);

// Enhanced Publication Card Component
const EnhancedPublicationCard = ({ 
  publication, 
  packages, 
  viewMode, 
  isWishlisted, 
  isComparing,
  onToggleWishlist, 
  onToggleCompare 
}) => {
  const [showPackages, setShowPackages] = useState(false);
  
  const minPrice = packages && packages.length > 0 
    ? Math.min(...packages.map(p => p.price))
    : 0;

  const fastPackage = packages?.find(p => p.name === 'Fast');
  const tatDays = fastPackage ? `${fastPackage.tat_days_min}-${fastPackage.tat_days_max} days` : 'Varies';
  const hasValidLogo = publication.logo && !publication.logo.includes('placeholder');

  if (viewMode === 'list') {
    return (
      <div 
        className="group border border-[#e5e5e5] p-4 bg-white hover:border-[#D4AF37] hover:shadow-lg transition-all duration-300"
        data-testid={`publication-card-${publication.id}`}
      >
        <div className="flex items-center gap-6">
          {/* Logo */}
          <div className="h-12 w-32 flex-shrink-0 flex items-center">
            {hasValidLogo ? (
              <img 
                src={publication.logo} 
                alt={publication.name}
                className="h-10 max-w-full object-contain"
              />
            ) : (
              <span className="font-playfair text-base font-bold text-[#1a1a1a]">
                {publication.name}
              </span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            {hasValidLogo && (
              <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] truncate">
                {publication.name}
              </h3>
            )}
            <p className="font-manrope text-sm text-[#737373] truncate">
              {publication.description}
            </p>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 flex-shrink-0">
            {publication.da && (
              <span className="inline-block px-2 py-1 bg-[#fee2e2] text-[#D4AF37] font-mono text-xs font-bold">
                DA {publication.da}
              </span>
            )}
            {publication.backlink !== false && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#dcfce7] text-[#16a34a] text-xs font-manrope">
                <LinkIcon className="w-3 h-3" />
                Backlink
              </span>
            )}
          </div>

          {/* Price */}
          <div className="text-right flex-shrink-0 w-32">
            <span className="font-mono font-bold text-lg text-[#1a1a1a]">
              ₹{minPrice.toLocaleString('en-IN')}
            </span>
            <p className="text-xs text-[#D4AF37]">+ GST</p>
            <p className="text-xs text-[#737373]">{tatDays}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onToggleWishlist(publication.id)}
              className={`p-2 border transition-all ${
                isWishlisted 
                  ? 'border-[#D4AF37] bg-[#fee2e2] text-[#D4AF37]' 
                  : 'border-[#e5e5e5] hover:border-[#D4AF37] text-[#737373] hover:text-[#D4AF37]'
              }`}
              data-testid={`wishlist-btn-${publication.id}`}
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={() => onToggleCompare(publication.id)}
              className={`p-2 border transition-all ${
                isComparing 
                  ? 'border-[#3b82f6] bg-[#dbeafe] text-[#3b82f6]' 
                  : 'border-[#e5e5e5] hover:border-[#3b82f6] text-[#737373] hover:text-[#3b82f6]'
              }`}
              data-testid={`compare-btn-${publication.id}`}
            >
              <GitCompare className="w-4 h-4" />
            </button>
            <Link 
              to={`/publication/${publication.id}`}
              className="px-4 py-2 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-colors"
            >
              View
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Grid View (Card)
  return (
    <div 
      className="group relative border border-[#e5e5e5] bg-white hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300"
      data-testid={`publication-card-${publication.id}`}
      onMouseEnter={() => setShowPackages(true)}
      onMouseLeave={() => setShowPackages(false)}
    >
      {/* Quick Action Buttons */}
      <div className="absolute top-3 right-3 flex gap-2 z-10">
        <button
          onClick={(e) => { e.preventDefault(); onToggleWishlist(publication.id); }}
          className={`p-2 rounded-full shadow-md transition-all ${
            isWishlisted 
              ? 'bg-[#D4AF37] text-[#0d0d0d]' 
              : 'bg-white text-[#737373] hover:text-[#D4AF37] hover:bg-[#fee2e2]'
          }`}
          data-testid={`wishlist-btn-${publication.id}`}
        >
          <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
        </button>
        <button
          onClick={(e) => { e.preventDefault(); onToggleCompare(publication.id); }}
          className={`p-2 rounded-full shadow-md transition-all ${
            isComparing 
              ? 'bg-[#3b82f6] text-white' 
              : 'bg-white text-[#737373] hover:text-[#3b82f6] hover:bg-[#dbeafe]'
          }`}
          data-testid={`compare-btn-${publication.id}`}
        >
          <GitCompare className="w-4 h-4" />
        </button>
      </div>

      <div className="p-6">
        {/* Logo Section */}
        <div className="h-20 mb-4 flex items-center justify-center bg-[#fafafa] rounded-lg p-4">
          {hasValidLogo ? (
            <img 
              src={publication.logo} 
              alt={publication.name}
              className="h-14 max-w-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : null}
          <div 
            className={`font-playfair text-xl font-bold text-[#1a1a1a] text-center ${hasValidLogo ? 'hidden' : ''}`}
            style={{ display: hasValidLogo ? 'none' : 'block' }}
          >
            {publication.name}
          </div>
        </div>

        {/* Name (if logo exists) */}
        {hasValidLogo && (
          <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-2 text-center">
            {publication.name}
          </h3>
        )}
        
        {/* Categories & DA */}
        <div className="flex flex-wrap justify-center gap-1.5 mb-4">
          {(publication.categories || [publication.category]).filter(Boolean).slice(0, 2).map((cat, idx) => (
            <span key={idx} className="inline-block px-2 py-1 bg-[#f5f5f5] text-[#737373] font-manrope text-[10px] uppercase tracking-widest">
              {cat}
            </span>
          ))}
          {publication.da && (
            <span className="inline-block px-2 py-1 bg-[#fee2e2] text-[#D4AF37] font-mono text-[10px] font-bold">
              DA {publication.da}
            </span>
          )}
        </div>
        
        {/* Description */}
        <p className="font-manrope text-sm text-[#737373] mb-4 line-clamp-2 text-center">
          {publication.description}
        </p>

        {/* Feature Tags */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#f5f5f5] text-[#737373] text-xs font-manrope">
            <Globe className="w-3 h-3" />
            {publication.language || 'English'}
          </span>
          {publication.backlink !== false && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#dcfce7] text-[#16a34a] text-xs font-manrope">
              <LinkIcon className="w-3 h-3" />
              Backlink
            </span>
          )}
          {publication.disclaimer && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#fef3c7] text-[#d97706] text-xs font-manrope">
              <AlertCircle className="w-3 h-3" />
              Disclaimer
            </span>
          )}
        </div>
        
        {/* Price & TAT */}
        <div className="border-t border-[#e5e5e5] pt-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="font-manrope text-xs text-[#737373]">Starting from</span>
            <div className="text-right">
              <span className="font-mono font-bold text-xl text-[#1a1a1a]">₹{minPrice.toLocaleString('en-IN')}</span>
              <span className="text-xs text-[#D4AF37] ml-1">+ GST</span>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-manrope text-xs text-[#737373]">Turnaround</span>
            <span className="font-manrope text-sm text-[#737373]">{tatDays}</span>
          </div>
        </div>
        
        {/* CTA Button */}
        <Link 
          to={`/publication/${publication.id}`}
          data-testid={`view-details-button-${publication.id}`}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-colors group"
        >
          View Details
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      {/* Hover Package Preview */}
      {showPackages && packages && packages.length > 0 && (
        <div className="absolute left-0 right-0 bottom-full mb-2 bg-white border border-[#e5e5e5] shadow-xl p-4 z-20 rounded-t-lg">
          <p className="font-manrope text-xs text-[#737373] uppercase tracking-widest mb-3">Available Packages</p>
          <div className="space-y-2">
            {packages.slice(0, 3).map((pkg, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm">
                <span className="font-manrope text-[#1a1a1a]">{pkg.name}</span>
                <span className="font-mono font-semibold text-[#D4AF37]">₹{pkg.price.toLocaleString('en-IN')} <span className="text-xs font-normal">+GST</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Compare Modal Component
const CompareModal = ({ publications, packages, onClose, onRemove }) => {
  if (publications.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#D4AF37] shadow-2xl z-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-manrope font-semibold text-[#1a1a1a]">
              Compare ({publications.length}/4)
            </span>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#1a1a1a]">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-4 gap-4">
          {publications.map(pub => {
            const pubPackages = packages[pub.id] || [];
            const minPrice = pubPackages.length > 0 ? Math.min(...pubPackages.map(p => p.price)) : 0;
            
            return (
              <div key={pub.id} className="border border-[#e5e5e5] p-3 relative">
                <button 
                  onClick={() => onRemove(pub.id)}
                  className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0d0d0d] rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
                <h4 className="font-playfair font-bold text-sm truncate">{pub.name}</h4>
                <p className="text-xs text-[#737373]">DA: {pub.da || 'N/A'}</p>
                <p className="font-mono text-sm font-bold text-[#D4AF37]">₹{minPrice.toLocaleString('en-IN')} <span className="text-xs font-normal">+GST</span></p>
              </div>
            );
          })}
          {[...Array(4 - publications.length)].map((_, i) => (
            <div key={i} className="border border-dashed border-[#e5e5e5] p-3 flex items-center justify-center text-[#a3a3a3] text-sm">
              Add to compare
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Social Page Card Component
const SocialPageCard = ({ page }) => {
  const minPrice = page.packages && page.packages.length > 0 
    ? Math.min(...page.packages.map(p => p.price))
    : 0;

  const platformColors = {
    instagram: 'from-purple-600 to-pink-500',
    facebook: 'bg-[#1877f2]',
    twitter: 'bg-black',
    linkedin: 'bg-[#0077b5]',
    youtube: 'bg-[#ff0000]',
  };

  const formatFollowers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count;
  };

  return (
    <div className="border border-[#e5e5e5] p-6 bg-white hover:border-[#D4AF37] hover:shadow-lg transition-all group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {page.logo ? (
            <img src={page.logo} alt={page.name} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${platformColors[page.platform] || 'bg-gray-400'} flex items-center justify-center`}>
              <Share2 className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h3 className="font-playfair font-bold text-lg text-[#1a1a1a]">{page.name}</h3>
            <p className="text-sm text-[#737373]">@{page.handle}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-semibold text-white bg-gradient-to-r ${platformColors[page.platform] || 'bg-gray-500'}`}>
          {page.platform?.charAt(0).toUpperCase() + page.platform?.slice(1)}
        </span>
      </div>

      <div className="flex gap-4 mb-4 text-sm">
        <div>
          <span className="font-bold text-[#1a1a1a]">{formatFollowers(page.followers)}</span>
          <span className="text-[#737373] ml-1">followers</span>
        </div>
        {page.engagement_rate && (
          <div>
            <span className="font-bold text-[#1a1a1a]">{page.engagement_rate}%</span>
            <span className="text-[#737373] ml-1">engagement</span>
          </div>
        )}
      </div>

      {page.categories && page.categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {page.categories.slice(0, 3).map((cat, idx) => (
            <span key={idx} className="px-2 py-1 bg-gray-100 text-[#737373] text-xs rounded">
              {cat}
            </span>
          ))}
        </div>
      )}

      <div className="border-t border-[#e5e5e5] pt-4 mt-4">
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs text-[#737373] uppercase tracking-widest">Starting from</span>
          <div className="text-right">
            <span className="font-mono font-bold text-lg text-[#D4AF37]">
              ₹{minPrice.toLocaleString('en-IN')}
            </span>
            <span className="text-xs text-[#D4AF37] ml-1">+GST</span>
          </div>
        </div>
        <Link
          to={`/social-page/${page.id}`}
          className="block w-full py-3 bg-[#1a1a1a] text-white text-center font-manrope text-sm uppercase tracking-widest hover:bg-[#D4AF37] transition-colors"
        >
          View Packages
        </Link>
      </div>
    </div>
  );
};

// Bundle Card Component
const BundleCard = ({ bundle }) => {
  const discountPercent = bundle.discount_percent || 
    (bundle.original_price > 0 ? Math.round((1 - bundle.bundle_price / bundle.original_price) * 100) : 0);

  return (
    <div className="border border-[#e5e5e5] p-6 bg-white hover:border-[#D4AF37] hover:shadow-lg transition-all group relative overflow-hidden">
      {discountPercent > 0 && (
        <div className="absolute top-4 right-4 bg-[#D4AF37] text-[#0d0d0d] px-3 py-1 text-sm font-bold">
          {discountPercent}% OFF
        </div>
      )}

      <div className="mb-4">
        {bundle.image ? (
          <img src={bundle.image} alt={bundle.name} className="w-full h-32 object-cover rounded" />
        ) : (
          <div className="w-full h-32 bg-gradient-to-br from-[#1a1a1a] to-[#404040] rounded flex items-center justify-center">
            <Package className="w-12 h-12 text-white/50" />
          </div>
        )}
      </div>

      <span className="inline-block px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium rounded mb-2">
        {bundle.bundle_type?.replace(/_/g, ' ').toUpperCase()}
      </span>

      <h3 className="font-playfair font-bold text-lg text-[#1a1a1a] mb-2">{bundle.name}</h3>
      <p className="text-sm text-[#737373] mb-4 line-clamp-2">{bundle.short_description || bundle.description}</p>

      {bundle.features && bundle.features.length > 0 && (
        <ul className="space-y-1 mb-4">
          {bundle.features.slice(0, 3).map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-[#737373]">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      )}

      {bundle.guaranteed_links && (
        <div className="flex items-center gap-2 mb-4 text-sm">
          <LinkIcon className="w-4 h-4 text-[#D4AF37]" />
          <span className="font-semibold">{bundle.guaranteed_links}+ Links Guaranteed</span>
        </div>
      )}

      <div className="border-t border-[#e5e5e5] pt-4 mt-4">
        <div className="flex items-baseline gap-2 mb-3">
          {bundle.original_price !== bundle.bundle_price && (
            <span className="text-sm text-[#737373] line-through">
              ₹{bundle.original_price?.toLocaleString('en-IN')}
            </span>
          )}
          <span className="font-mono font-bold text-xl text-[#D4AF37]">
            ₹{bundle.bundle_price?.toLocaleString('en-IN')}
          </span>
          <span className="text-xs text-[#D4AF37]">+GST</span>
        </div>
        <Link
          to={`/bundle/${bundle.slug || bundle.id}`}
          className="block w-full py-3 bg-[#1a1a1a] text-white text-center font-manrope text-sm uppercase tracking-widest hover:bg-[#D4AF37] transition-colors"
        >
          View Bundle
        </Link>
      </div>
    </div>
  );
};

// Print Media Card Component
const PrintMediaCard = ({ media }) => {
  const minPrice = media.packages && media.packages.length > 0 
    ? Math.min(...media.packages.map(p => p.price))
    : 0;

  const formatCirculation = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
    return count;
  };

  return (
    <div className="border border-[#e5e5e5] p-6 bg-white hover:border-[#D4AF37] hover:shadow-lg transition-all group">
      <div className="flex items-start justify-between mb-4">
        {media.logo ? (
          <img src={media.logo} alt={media.name} className="h-12 max-w-[150px] object-contain" />
        ) : (
          <h3 className="font-playfair font-bold text-lg text-[#1a1a1a]">{media.name}</h3>
        )}
        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">
          {media.media_type?.charAt(0).toUpperCase() + media.media_type?.slice(1)}
        </span>
      </div>

      {media.logo && (
        <h3 className="font-playfair font-bold text-lg text-[#1a1a1a] mb-2">{media.name}</h3>
      )}

      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4 text-[#737373]" />
          <span className="font-semibold">{formatCirculation(media.circulation)}</span>
          <span className="text-[#737373]">circulation</span>
        </div>
        <span className="text-[#737373]">{media.frequency?.replace(/_/g, ' ')}</span>
      </div>

      {media.distribution_regions && media.distribution_regions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {media.distribution_regions.slice(0, 3).map((region, idx) => (
            <span key={idx} className="px-2 py-1 bg-gray-100 text-[#737373] text-xs rounded">
              {region}
            </span>
          ))}
        </div>
      )}

      <div className="border-t border-[#e5e5e5] pt-4 mt-4">
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs text-[#737373] uppercase tracking-widest">Starting from</span>
          <div className="text-right">
            <span className="font-mono font-bold text-lg text-[#D4AF37]">
              ₹{minPrice.toLocaleString('en-IN')}
            </span>
            <span className="text-xs text-[#D4AF37] ml-1">+5% GST</span>
          </div>
        </div>
        <Link
          to={`/print-media/${media.id}`}
          className="block w-full py-3 bg-[#1a1a1a] text-white text-center font-manrope text-sm uppercase tracking-widest hover:bg-[#D4AF37] transition-colors"
        >
          View Options
        </Link>
      </div>
    </div>
  );
};

const Marketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeServiceType, setActiveServiceType] = useState(searchParams.get('type') || 'all');
  const [publications, setPublications] = useState([]);
  const [socialPages, setSocialPages] = useState([]);
  const [printMedia, setPrintMedia] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [packagesMap, setPackagesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [wishlist, setWishlist] = useState(() => {
    const saved = localStorage.getItem('publication_wishlist');
    return saved ? JSON.parse(saved) : [];
  });
  const [compareList, setCompareList] = useState([]);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: '',
    maxPrice: '',
    // Digital PR specific
    minDA: '',
    maxDA: '',
    language: '',
    hasBacklink: false,
    noDisclaimer: false,
    // Social Media specific
    platform: '',
    pageType: '',
    minFollowers: '',
    maxFollowers: '',
    // Print Media specific
    mediaType: '',
    minCirculation: '',
    // Bundles specific
    bundleType: ''
  });

  const categories = [
    'Business', 'Startup', 'Tech', 'Real Estate', 'Healthcare', 
    'Lifestyle', 'Entertainment', 'Finance', 'Education', 'Sports'
  ];

  const languages = ['English', 'Hindi', 'Regional'];

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    // Update URL when service type changes
    const params = new URLSearchParams(searchParams);
    if (activeServiceType !== 'all') {
      params.set('type', activeServiceType);
    } else {
      params.delete('type');
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServiceType]);

  useEffect(() => {
    localStorage.setItem('publication_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [pubResponse, socialResponse, printResponse, bundleResponse] = await Promise.all([
        axios.get(`${API}/publications`),
        axios.get(`${API}/social-pages`).catch(() => ({ data: [] })),
        axios.get(`${API}/print-media`).catch(() => ({ data: [] })),
        axios.get(`${API}/bundles`).catch(() => ({ data: [] })),
      ]);
      
      setPublications(pubResponse.data);
      setSocialPages(socialResponse.data);
      setPrintMedia(printResponse.data);
      setBundles(bundleResponse.data);
      
      // Fetch packages for publications
      const packagesPromises = pubResponse.data.map(pub => 
        axios.get(`${API}/publications/${pub.id}/packages`)
      );
      const packagesResponses = await Promise.all(packagesPromises);
      
      const newPackagesMap = {};
      pubResponse.data.forEach((pub, index) => {
        newPackagesMap[pub.id] = packagesResponses[index].data;
      });
      setPackagesMap(newPackagesMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load marketplace data');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceTypeChange = (type) => {
    setActiveServiceType(type);
    setSearchQuery('');
    setFilters(prev => ({ ...prev, category: '' }));
  };

  // Filter and sort publications
  const filteredPublications = useMemo(() => {
    let result = [...publications];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(pub => 
        pub.name.toLowerCase().includes(query) ||
        pub.description?.toLowerCase().includes(query) ||
        pub.category?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (filters.category) {
      result = result.filter(pub => 
        pub.category === filters.category || 
        (pub.categories && pub.categories.includes(filters.category))
      );
    }

    // Price filters
    if (filters.minPrice || filters.maxPrice) {
      result = result.filter(pub => {
        const pubPackages = packagesMap[pub.id] || [];
        if (pubPackages.length === 0) return false;
        const minPubPrice = Math.min(...pubPackages.map(p => p.price));
        if (filters.minPrice && minPubPrice < parseInt(filters.minPrice)) return false;
        if (filters.maxPrice && minPubPrice > parseInt(filters.maxPrice)) return false;
        return true;
      });
    }

    // DA filters
    if (filters.minDA) {
      result = result.filter(pub => pub.da && pub.da >= parseInt(filters.minDA));
    }
    if (filters.maxDA) {
      result = result.filter(pub => pub.da && pub.da <= parseInt(filters.maxDA));
    }

    // Language filter
    if (filters.language) {
      result = result.filter(pub => 
        (pub.language || 'English') === filters.language
      );
    }

    // Feature filters
    if (filters.hasBacklink) {
      result = result.filter(pub => pub.backlink !== false);
    }
    if (filters.noDisclaimer) {
      result = result.filter(pub => !pub.disclaimer);
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => {
          const aMin = Math.min(...(packagesMap[a.id] || [{ price: 0 }]).map(p => p.price));
          const bMin = Math.min(...(packagesMap[b.id] || [{ price: 0 }]).map(p => p.price));
          return aMin - bMin;
        });
        break;
      case 'price-high':
        result.sort((a, b) => {
          const aMin = Math.min(...(packagesMap[a.id] || [{ price: 0 }]).map(p => p.price));
          const bMin = Math.min(...(packagesMap[b.id] || [{ price: 0 }]).map(p => p.price));
          return bMin - aMin;
        });
        break;
      case 'da-high':
        result.sort((a, b) => (b.da || 0) - (a.da || 0));
        break;
      case 'da-low':
        result.sort((a, b) => (a.da || 0) - (b.da || 0));
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [publications, packagesMap, searchQuery, filters, sortBy]);

  // Filter social pages
  const filteredSocialPages = useMemo(() => {
    let result = [...socialPages];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(page => 
        page.name?.toLowerCase().includes(query) ||
        page.description?.toLowerCase().includes(query) ||
        page.platform?.toLowerCase().includes(query)
      );
    }

    // Price filter
    if (filters.minPrice) {
      result = result.filter(page => page.price >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      result = result.filter(page => page.price <= parseInt(filters.maxPrice));
    }

    // Platform filter
    if (filters.platform) {
      result = result.filter(page => 
        page.platform?.toLowerCase() === filters.platform.toLowerCase()
      );
    }

    // Page type filter
    if (filters.pageType) {
      result = result.filter(page => page.page_type === filters.pageType);
    }

    // Followers filter
    if (filters.minFollowers) {
      result = result.filter(page => 
        (page.followers || 0) >= parseInt(filters.minFollowers)
      );
    }
    if (filters.maxFollowers) {
      result = result.filter(page => 
        (page.followers || 0) <= parseInt(filters.maxFollowers)
      );
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-high':
        result.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'followers-high':
        result.sort((a, b) => (b.followers || 0) - (a.followers || 0));
        break;
      case 'followers-low':
        result.sort((a, b) => (a.followers || 0) - (b.followers || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default:
        break;
    }

    return result;
  }, [socialPages, searchQuery, filters, sortBy]);

  // Filter print media
  const filteredPrintMedia = useMemo(() => {
    let result = [...printMedia];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(media => 
        media.name?.toLowerCase().includes(query) ||
        media.description?.toLowerCase().includes(query)
      );
    }

    // Price filter
    if (filters.minPrice) {
      result = result.filter(media => media.price >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      result = result.filter(media => media.price <= parseInt(filters.maxPrice));
    }

    // Media type filter
    if (filters.mediaType) {
      result = result.filter(media => media.media_type === filters.mediaType);
    }

    // Circulation filter
    if (filters.minCirculation) {
      result = result.filter(media => 
        (media.circulation || 0) >= parseInt(filters.minCirculation)
      );
    }

    // Language filter
    if (filters.language) {
      result = result.filter(media => 
        (media.language || 'English') === filters.language
      );
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-high':
        result.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'circulation-high':
        result.sort((a, b) => (b.circulation || 0) - (a.circulation || 0));
        break;
      case 'circulation-low':
        result.sort((a, b) => (a.circulation || 0) - (b.circulation || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default:
        break;
    }

    return result;
  }, [printMedia, searchQuery, filters, sortBy]);

  // Filter bundles
  const filteredBundles = useMemo(() => {
    let result = [...bundles];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(bundle => 
        bundle.name?.toLowerCase().includes(query) ||
        bundle.description?.toLowerCase().includes(query)
      );
    }

    // Price filter
    if (filters.minPrice) {
      result = result.filter(bundle => bundle.price >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      result = result.filter(bundle => bundle.price <= parseInt(filters.maxPrice));
    }

    // Bundle type filter
    if (filters.bundleType) {
      result = result.filter(bundle => bundle.bundle_type === filters.bundleType);
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-high':
        result.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default:
        break;
    }

    return result;
  }, [bundles, searchQuery, filters, sortBy]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      category: '',
      minPrice: '',
      maxPrice: '',
      // Digital PR specific
      minDA: '',
      maxDA: '',
      language: '',
      hasBacklink: false,
      noDisclaimer: false,
      // Social Media specific
      platform: '',
      pageType: '',
      minFollowers: '',
      maxFollowers: '',
      // Print Media specific
      mediaType: '',
      minCirculation: '',
      // Bundles specific
      bundleType: ''
    });
    setSearchQuery('');
    setSortBy('default');
  };

  const toggleWishlist = (pubId) => {
    setWishlist(prev => {
      if (prev.includes(pubId)) {
        toast.success('Removed from wishlist');
        return prev.filter(id => id !== pubId);
      } else {
        toast.success('Added to wishlist');
        return [...prev, pubId];
      }
    });
  };

  const toggleCompare = (pubId) => {
    setCompareList(prev => {
      if (prev.includes(pubId)) {
        return prev.filter(id => id !== pubId);
      } else if (prev.length < 4) {
        return [...prev, pubId];
      } else {
        toast.error('Maximum 4 items can be compared');
        return prev;
      }
    });
  };

  const activeFiltersCount = [
    filters.category,
    filters.minPrice,
    filters.maxPrice,
    filters.minDA,
    filters.maxDA,
    filters.language,
    filters.hasBacklink,
    filters.noDisclaimer,
    filters.platform,
    filters.pageType,
    filters.minFollowers,
    filters.maxFollowers,
    filters.mediaType,
    filters.minCirculation,
    filters.bundleType
  ].filter(Boolean).length;

  // Get the current result count based on active service type
  const getResultsCount = () => {
    switch (activeServiceType) {
      case 'digital_pr':
        return filteredPublications.length;
      case 'social_media':
        return filteredSocialPages.length;
      case 'print_media':
        return filteredPrintMedia.length;
      case 'bundles':
        return filteredBundles.length;
      default:
        return filteredPublications.length + filteredSocialPages.length + filteredPrintMedia.length + filteredBundles.length;
    }
  };

  const getResultsLabel = () => {
    switch (activeServiceType) {
      case 'digital_pr':
        return 'publications';
      case 'social_media':
        return 'social pages';
      case 'print_media':
        return 'print media';
      case 'bundles':
        return 'bundles';
      default:
        return 'items';
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <MarketplaceSEO />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#1a1a1a]">
            Media Marketplace
          </h1>
          <p className="font-manrope text-base text-[#737373] mb-6">
            Browse and select from verified media outlets across India
          </p>

          {/* Service Type Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 p-1 bg-white border border-[#e5e5e5] rounded-xl inline-flex">
            {SERVICE_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleServiceTypeChange(tab.id)}
                data-testid={`service-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-manrope text-sm font-medium transition-all ${
                  activeServiceType === tab.id 
                    ? 'bg-[#D4AF37] text-[#0d0d0d] shadow-sm' 
                    : 'text-[#737373] hover:text-[#1a1a1a] hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
            <input
              type="text"
              placeholder={
                activeServiceType === 'social_media' ? "Search social pages by name, platform..." :
                activeServiceType === 'print_media' ? "Search magazines, newspapers..." :
                activeServiceType === 'bundles' ? "Search bundles and packages..." :
                "Search publications by name, category..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              className="w-full pl-12 pr-4 py-3 border border-[#e5e5e5] bg-white text-[#1a1a1a] font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] focus:outline-none rounded-lg"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1a1a1a]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Quick Category Pills - Only for Digital PR */}
        {(activeServiceType === 'digital_pr') && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => handleFilterChange('category', '')}
              className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                !filters.category 
                  ? 'bg-[#D4AF37] text-[#0d0d0d]' 
                  : 'bg-white border border-[#e5e5e5] text-[#737373] hover:border-[#D4AF37] hover:text-[#D4AF37]'
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => handleFilterChange('category', filters.category === cat ? '' : cat)}
                className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                  filters.category === cat 
                    ? 'bg-[#D4AF37] text-[#0d0d0d]' 
                    : 'bg-white border border-[#e5e5e5] text-[#737373] hover:border-[#D4AF37] hover:text-[#D4AF37]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Controls Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-white p-4 border border-[#e5e5e5] rounded-lg">
          <div className="flex items-center gap-4">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-manrope text-sm transition-all ${
                showFilters ? 'border-[#D4AF37] text-[#D4AF37] bg-[#fee2e2]' : 'border-[#e5e5e5] text-[#737373] hover:border-[#D4AF37]'
              }`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="bg-[#D4AF37] text-[#0d0d0d] text-xs px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Results Count */}
            <span className="font-manrope text-sm text-[#737373]">
              <span className="font-bold text-[#1a1a1a]">{getResultsCount()}</span> {getResultsLabel()} found
            </span>
          </div>

          <div className="flex items-center gap-4">
            {/* Sort Dropdown - Service Type Specific */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
                className="appearance-none pl-4 pr-10 py-2 border border-[#e5e5e5] bg-white text-[#1a1a1a] font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded-lg cursor-pointer"
              >
                <option value="default">Sort by: Default</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                {/* DA options only for Digital PR */}
                {(activeServiceType === 'digital_pr') && (
                  <>
                    <option value="da-high">DA: High to Low</option>
                    <option value="da-low">DA: Low to High</option>
                  </>
                )}
                {/* Followers options only for Social Media */}
                {(activeServiceType === 'social_media') && (
                  <>
                    <option value="followers-high">Followers: High to Low</option>
                    <option value="followers-low">Followers: Low to High</option>
                  </>
                )}
                {/* Circulation options only for Print Media */}
                {(activeServiceType === 'print_media') && (
                  <>
                    <option value="circulation-high">Circulation: High to Low</option>
                    <option value="circulation-low">Circulation: Low to High</option>
                  </>
                )}
                <option value="name">Name: A-Z</option>
              </select>
              <ArrowUpDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373] pointer-events-none" />
            </div>

            {/* View Toggle - Only for Digital PR which has list view */}
            {(activeServiceType === 'all' || activeServiceType === 'digital_pr') && (
              <div className="flex border border-[#e5e5e5] rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 transition-all ${viewMode === 'grid' ? 'bg-[#D4AF37] text-[#0d0d0d]' : 'bg-white text-[#737373] hover:bg-[#f5f5f5]'}`}
                  data-testid="grid-view-btn"
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 transition-all ${viewMode === 'list' ? 'bg-[#D4AF37] text-[#0d0d0d]' : 'bg-white text-[#737373] hover:bg-[#f5f5f5]'}`}
                  data-testid="list-view-btn"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Advanced Filters Panel - Service Type Specific */}
        {showFilters && (
          <div className="mb-8 bg-white border border-[#e5e5e5] p-6 rounded-lg animate-in slide-in-from-top-2">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-manrope font-semibold uppercase text-sm tracking-widest text-[#1a1a1a]">
                  Advanced Filters
                </span>
              </div>
              <button
                onClick={clearAllFilters}
                data-testid="clear-filters-button"
                className="text-[#D4AF37] font-manrope text-sm hover:underline"
              >
                Clear All
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Price Range - Common for all */}
              <div>
                <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                  Price Range (₹)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    data-testid="min-price-filter"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    placeholder="Min"
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                  />
                  <input
                    type="number"
                    data-testid="max-price-filter"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    placeholder="Max"
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                  />
                </div>
              </div>

              {/* DA Range - Only for Digital PR */}
              {(activeServiceType === 'digital_pr') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Domain Authority
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={filters.minDA}
                      onChange={(e) => handleFilterChange('minDA', e.target.value)}
                      placeholder="Min DA"
                      min="0"
                      max="100"
                      className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                    />
                    <input
                      type="number"
                      value={filters.maxDA}
                      onChange={(e) => handleFilterChange('maxDA', e.target.value)}
                      placeholder="Max DA"
                      min="0"
                      max="100"
                      className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                    />
                  </div>
                </div>
              )}

              {/* Followers Range - Only for Social Media */}
              {(activeServiceType === 'social_media') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Followers
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      data-testid="min-followers-filter"
                      value={filters.minFollowers || ''}
                      onChange={(e) => handleFilterChange('minFollowers', e.target.value)}
                      placeholder="Min"
                      className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                    />
                    <input
                      type="number"
                      data-testid="max-followers-filter"
                      value={filters.maxFollowers || ''}
                      onChange={(e) => handleFilterChange('maxFollowers', e.target.value)}
                      placeholder="Max"
                      className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                    />
                  </div>
                </div>
              )}

              {/* Platform - Only for Social Media */}
              {(activeServiceType === 'social_media') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Platform
                  </label>
                  <select
                    data-testid="platform-filter"
                    value={filters.platform || ''}
                    onChange={(e) => handleFilterChange('platform', e.target.value)}
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded cursor-pointer"
                  >
                    <option value="">All Platforms</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="twitter">Twitter/X</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>
              )}

              {/* Circulation - Only for Print Media */}
              {(activeServiceType === 'print_media') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Min Circulation
                  </label>
                  <input
                    type="number"
                    data-testid="min-circulation-filter"
                    value={filters.minCirculation || ''}
                    onChange={(e) => handleFilterChange('minCirculation', e.target.value)}
                    placeholder="e.g., 50000"
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded"
                  />
                </div>
              )}

              {/* Media Type - Only for Print Media */}
              {(activeServiceType === 'print_media') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Media Type
                  </label>
                  <select
                    data-testid="media-type-filter"
                    value={filters.mediaType || ''}
                    onChange={(e) => handleFilterChange('mediaType', e.target.value)}
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded cursor-pointer"
                  >
                    <option value="">All Types</option>
                    <option value="magazine">Magazine</option>
                    <option value="newspaper">Newspaper</option>
                  </select>
                </div>
              )}

              {/* Bundle Type - Only for Bundles */}
              {(activeServiceType === 'bundles') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Bundle Type
                  </label>
                  <select
                    data-testid="bundle-type-filter"
                    value={filters.bundleType || ''}
                    onChange={(e) => handleFilterChange('bundleType', e.target.value)}
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded cursor-pointer"
                  >
                    <option value="">All Bundles</option>
                    <option value="publication_bundle">Publication Bundle</option>
                    <option value="press_release_bundle">Press Release Bundle</option>
                    <option value="social_bundle">Social Bundle</option>
                    <option value="cross_service">Cross Service</option>
                  </select>
                </div>
              )}

              {/* Language - For Digital PR and Print Media */}
              {(activeServiceType === 'digital_pr' || activeServiceType === 'print_media') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Language
                  </label>
                  <select
                    data-testid="language-filter"
                    value={filters.language}
                    onChange={(e) => handleFilterChange('language', e.target.value)}
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded cursor-pointer"
                  >
                    <option value="">All Languages</option>
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Feature Toggles - Only for Digital PR */}
              {(activeServiceType === 'digital_pr') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Features
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.hasBacklink}
                        onChange={(e) => handleFilterChange('hasBacklink', e.target.checked)}
                        className="w-4 h-4 text-[#D4AF37] border-[#e5e5e5] rounded focus:ring-[#D4AF37]"
                      />
                      <span className="font-manrope text-sm text-[#1a1a1a]">Backlink Included</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.noDisclaimer}
                        onChange={(e) => handleFilterChange('noDisclaimer', e.target.checked)}
                        className="w-4 h-4 text-[#D4AF37] border-[#e5e5e5] rounded focus:ring-[#D4AF37]"
                      />
                      <span className="font-manrope text-sm text-[#1a1a1a]">No Disclaimer Tag</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Page Type - Only for Social Media */}
              {(activeServiceType === 'social_media') && (
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">
                    Page Type
                  </label>
                  <select
                    data-testid="page-type-filter"
                    value={filters.pageType || ''}
                    onChange={(e) => handleFilterChange('pageType', e.target.value)}
                    className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] px-3 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none rounded cursor-pointer"
                  >
                    <option value="">All Types</option>
                    <option value="meme_page">Meme Page</option>
                    <option value="influencer">Influencer</option>
                    <option value="news_media">News/Media</option>
                    <option value="brand">Brand</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results Grid/List - Shows different content based on service type */}
        {loading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <PublicationCardSkeleton key={i} />)}
            </div>
          ) : (
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => <PublicationListSkeleton key={i} />)}
            </div>
          )
        ) : (
          <>
            {/* Bundles Section - Show when 'all' or 'bundles' */}
            {(activeServiceType === 'all' || activeServiceType === 'bundles') && bundles.length > 0 && (
              <div className="mb-12">
                {activeServiceType === 'all' && (
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a] flex items-center gap-2">
                      <Package className="w-6 h-6 text-[#D4AF37]" />
                      Featured Bundles
                    </h2>
                    <button
                      onClick={() => handleServiceTypeChange('bundles')}
                      className="text-[#D4AF37] font-manrope text-sm hover:underline flex items-center gap-1"
                    >
                      View All <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {activeServiceType === 'bundles' && filteredBundles.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                    <Package className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                    <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Bundles Found</h3>
                    <p className="font-manrope text-[#737373] mb-6">
                      Try adjusting your filters or search query
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors rounded-lg"
                    >
                      Clear All Filters
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(activeServiceType === 'all' ? filteredBundles.slice(0, 3) : filteredBundles)
                      .map(bundle => (
                        <BundleCard key={bundle.id} bundle={bundle} />
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Social Media Section - Show when 'all' or 'social_media' */}
            {(activeServiceType === 'all' || activeServiceType === 'social_media') && socialPages.length > 0 && (
              <div className="mb-12">
                {activeServiceType === 'all' && (
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a] flex items-center gap-2">
                      <Share2 className="w-6 h-6 text-[#D4AF37]" />
                      Social Media Pages
                    </h2>
                    <button
                      onClick={() => handleServiceTypeChange('social_media')}
                      className="text-[#D4AF37] font-manrope text-sm hover:underline flex items-center gap-1"
                    >
                      View All <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {activeServiceType === 'social_media' && filteredSocialPages.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                    <Share2 className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                    <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Social Pages Found</h3>
                    <p className="font-manrope text-[#737373] mb-6">
                      Try adjusting your filters or search query
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors rounded-lg"
                    >
                      Clear All Filters
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(activeServiceType === 'all' ? filteredSocialPages.slice(0, 3) : filteredSocialPages)
                      .map(page => (
                        <SocialPageCard key={page.id} page={page} />
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Print Media Section - TEMPORARILY HIDDEN
            {(activeServiceType === 'all' || activeServiceType === 'print_media') && printMedia.length > 0 && (
              <div className="mb-12">
                {activeServiceType === 'all' && (
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a] flex items-center gap-2">
                      <Printer className="w-6 h-6 text-[#D4AF37]" />
                      Print Media
                    </h2>
                    <button
                      onClick={() => handleServiceTypeChange('print_media')}
                      className="text-[#D4AF37] font-manrope text-sm hover:underline flex items-center gap-1"
                    >
                      View All <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {activeServiceType === 'print_media' && filteredPrintMedia.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                    <Printer className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                    <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Print Media Found</h3>
                    <p className="font-manrope text-[#737373] mb-6">
                      Try adjusting your filters or search query
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors rounded-lg"
                    >
                      Clear All Filters
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(activeServiceType === 'all' ? filteredPrintMedia.slice(0, 3) : filteredPrintMedia)
                      .map(media => (
                        <PrintMediaCard key={media.id} media={media} />
                      ))}
                  </div>
                )}
              </div>
            )}
            END TEMPORARILY HIDDEN */}

            {/* Digital PR Publications - Show when 'all' or 'digital_pr' */}
            {(activeServiceType === 'all' || activeServiceType === 'digital_pr') && (
              <div className="mb-12">
                {activeServiceType === 'all' && filteredPublications.length > 0 && (
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a] flex items-center gap-2">
                      <Newspaper className="w-6 h-6 text-[#D4AF37]" />
                      Digital PR Publications
                    </h2>
                    <button
                      onClick={() => handleServiceTypeChange('digital_pr')}
                      className="text-[#D4AF37] font-manrope text-sm hover:underline flex items-center gap-1"
                    >
                      View All <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {filteredPublications.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                    <Search className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                    <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Publications Found</h3>
                    <p className="font-manrope text-[#737373] mb-6">
                      Try adjusting your filters or search query
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors rounded-lg"
                    >
                      Clear All Filters
                    </button>
                  </div>
                ) : (
                  <div className={viewMode === 'grid' 
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    : "space-y-4"
                  }>
                    {(activeServiceType === 'all' ? filteredPublications.slice(0, 6) : filteredPublications).map(publication => (
                      <EnhancedPublicationCard 
                        key={publication.id}
                        publication={publication}
                        packages={packagesMap[publication.id] || []}
                        viewMode={viewMode}
                        isWishlisted={wishlist.includes(publication.id)}
                        isComparing={compareList.includes(publication.id)}
                        onToggleWishlist={toggleWishlist}
                        onToggleCompare={toggleCompare}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Empty state for specific service types */}
            {activeServiceType === 'social_media' && socialPages.length === 0 && (
              <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                <Share2 className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Social Pages Yet</h3>
                <p className="font-manrope text-[#737373]">
                  Social media promotions coming soon!
                </p>
              </div>
            )}

            {activeServiceType === 'print_media' && printMedia.length === 0 && (
              <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                <Printer className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Print Media Yet</h3>
                <p className="font-manrope text-[#737373]">
                  Print magazine placements coming soon!
                </p>
              </div>
            )}

            {activeServiceType === 'bundles' && bundles.length === 0 && (
              <div className="text-center py-16 bg-white border border-[#e5e5e5] rounded-lg">
                <Package className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
                <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">No Bundles Yet</h3>
                <p className="font-manrope text-[#737373]">
                  Bundle packages coming soon!
                </p>
              </div>
            )}
          </>
        )}

        {/* Wishlist Quick Access */}
        {wishlist.length > 0 && (
          <div className="fixed bottom-20 right-6 z-40">
            <button
              onClick={() => toast.info(`You have ${wishlist.length} saved publications`)}
              className="flex items-center gap-2 px-4 py-3 bg-[#D4AF37] text-[#0d0d0d] rounded-full shadow-lg hover:bg-[#B8960F] transition-colors"
            >
              <Heart className="w-5 h-5 fill-current" />
              <span className="font-manrope text-sm font-semibold">{wishlist.length}</span>
            </button>
          </div>
        )}
      </div>

      {/* Compare Modal */}
      {compareList.length > 0 && (
        <CompareModal
          publications={publications.filter(p => compareList.includes(p.id))}
          packages={packagesMap}
          onClose={() => setCompareList([])}
          onRemove={(id) => setCompareList(prev => prev.filter(i => i !== id))}
        />
      )}
      
      <Footer />
    </div>
  );
};

export default Marketplace;
